import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/MarketingLayout";
import AnimatedToolIcon from "@/components/AnimatedToolIcon";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";

function Icon({ name }) {
  const Cmp = Lucide[name] || Lucide.Sparkles;
  return <Cmp size={20} />;
}

export default function ToolsPage() {
  const [tools, setTools] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/tools").then((r) => setTools(r.data)).catch(() => {}); }, []);

  const cats = ["All", ...Array.from(new Set(tools.map((t) => t.category)))];
  const visible = tools
    .filter((t) => cat === "All" || t.category === cat)
    .filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()) || t.description.toLowerCase().includes(q.toLowerCase()));

  return (
    <MarketingLayout>
      <Seo title="AI Tools" path="/tools" description="Browse all 25+ AI tools on BINAREX.CO — AI writer, image generator, SEO tools, code generator and more." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-6">
        <div className="bx-chip"><Lucide.Wand2 size={12} /> 25+ AI TOOLS</div>
        <h1 className="bx-heading mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight">All AI Tools</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">Explore every AI tool on BINAREX.CO. Filter by category, search, and click a tool to start generating.</p>

        <div className="mt-7 flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
          <div className="relative max-w-md w-full">
            <Lucide.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input data-testid="tools-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tools…" className="bx-input pl-9" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`tools-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3.5 py-1.5 rounded-full text-xs transition ${
                  cat === c ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {visible.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.35, delay: (i % 12) * 0.03 }}
            >
              <Link to={`/tools/${t.slug}`} data-testid={`tools-card-${t.slug}`} className="bx-card p-5 group block">
                <AnimatedToolIcon name={t.icon} accent={["", "cyan", "blue", "emerald", "pink", "amber"][i % 6]} />
                <div className="bx-heading mt-4 text-lg font-semibold">{t.name}</div>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2">{t.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="text-slate-500">{t.category}</span>
                  <span className="text-purple-300 font-mono">{t.credit_cost} cr</span>
                </div>
              </Link>
            </motion.div>
          ))}
          {!visible.length && (
            <div className="col-span-full text-center text-slate-400 py-16">No tools match your search.</div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
